import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TranslocoService} from "@ngneat/transloco";
import {ActivatedRoute, Router} from "@angular/router";
import {DETAILS} from 'src/app/app-routing.module';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {UserDetail, TOAST_STATE, ADMINISTRATOR_ROLE} from "@model";
import {AdministratorsService, ExportService, ToastService, UserDetailService} from "@services";
import {FindOrganisersRequest} from 'src/app/model/find-organisers-request';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {environment} from '@environment';

@Component({
    selector: 'app-list-of-administrators',
    templateUrl: './list-of-administrators.component.html',
    styleUrls: ['./list-of-administrators.component.scss'],
    standalone: false
})
export class ListOfAdministratorsComponent extends AbstractComponent implements OnInit {
  @Input() administrators: UserDetail[] = [];
  @ViewChild('deleteDialog') deleteDialog: TemplateRef<UserDetail> = {} as TemplateRef<UserDetail>;
  filteredAdmins: UserDetail[] = [];
  currentUserDetails: UserDetail | undefined;
  name: string = "";

  constructor(private translocoService: TranslocoService, private modalService: NgbModal,
              private administratorService: AdministratorsService, private userDetailService: UserDetailService, private toastService: ToastService,
              private route: ActivatedRoute, private router: Router, private exportService: ExportService) {
    super();
  }

  ngOnInit() {
    this.subscribeToRouteDataChange();
    this.subscriptions.push(this.userDetailService.currentUserDetails.subscribe((userDetail: UserDetail | undefined) => {
      this.currentUserDetails = userDetail;
    }));
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.name = queryParams.name ? queryParams.name : "";
        this.filterAdministrators();
      })
    );
  }

  filterAdministrators() {
    let filtered: UserDetail[] = this.administrators.filter((admin: UserDetail) => this.filterFunction(admin));
    this.filteredAdmins = filtered.length > 0 ? filtered : [];
  }

  filterFunction(admin: UserDetail): boolean {
    if (this.name) {
      let filterName: string = this.name.toLowerCase() ?? "";
      return !!(admin.firstname?.toLowerCase().includes(filterName) || admin.lastname?.toLowerCase().includes(filterName) ||
        admin.email?.toLowerCase().includes(filterName));
    }
    return true;
  }

  openDeleteAdministratorDialog(user: UserDetail) {
    if (user.userId !== this.currentUserDetails?.userId) {
      const modalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
      });
      modalRef.componentInstance.title = this.translocoService.translate("administrator.list.delete.dialog.title");
      modalRef.componentInstance.text = this.translocoService.translate("administrator.list.delete.dialog.text");

      modalRef.result.then((_) => {
        this.onDeleteAdministrator(user);
      }, (_) => {
        /// ignore
      });
    }

  }

  onDeleteAdministrator(user: UserDetail): void {
    this.administratorService.deleteAdministrator(user.userId!).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("administrator.list.delete.success"));
        this.administratorService.findAdministrators(true).subscribe((users: UserDetail[]) => {
          this.administrators = users;
        })
      } else {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("administrator.list.delete.error"));
      }
    })
  }

  openAddNewOrganiserAdmin() {
    this.router.navigate([DETAILS], {queryParams: {type: 'addNewOrganiserAdmin'}, relativeTo: this.route});
  }

  openAddNewAdmin() {
    this.router.navigate([DETAILS], {queryParams: {type: 'addNewAdmin'}, relativeTo: this.route});
  }

  openAdminDetails(administrator: UserDetail): void {
    this.router.navigate([DETAILS], {queryParams: {userId: administrator.userId, type: 'viewDetails'}, relativeTo: this.route});
  }

  changeToOrganiserAdmin(administrator: UserDetail): void {
    this.router.navigate([DETAILS], {queryParams: {userId: administrator.userId, type: 'changeToOrganiserAdmin'}, relativeTo: this.route});
  }

  downloadAdmins(type: string) {
    this.exportService.downloadAdmins(type, this.createFilter());
  }

  createFilter() {
    return FindOrganisersRequest.createOrganisersRequest({text: this.name !== "" ? this.name : undefined});
  }

  confirmChangeRole(administrator: UserDetail, newRole: string): void {
    const roleLabel = newRole === ADMINISTRATOR_ROLE
      ? this.translocoService.translate('administrator.list.role.admin')
      : this.translocoService.translate('administrator.list.role.organiseradmin');
    const confirmText = this.translocoService.translate('administrator.list.change.role.confirm', { role: roleLabel });
    const modalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
    modalRef.componentInstance.title = this.translocoService.translate('administrator.list.change.role.title');
    modalRef.componentInstance.text = confirmText;
    modalRef.result.then(() => {
      this.changeUserRole(administrator, newRole);
    }, () => { /* ignore */ });
  }

  changeUserRole(administrator: UserDetail, newRole: string): void {
    if (newRole === ADMINISTRATOR_ROLE) {
      this.administratorService.changeAdminUserRole(administrator.userId!).subscribe((result: boolean) => {
        if (result) {
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('administrator.list.change.role.success'));
          this.administratorService.findAdministrators(true).subscribe((users: UserDetail[]) => {
            this.administrators = users;
          });
        } else {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('administrator.list.change.role.error'));
        }
      });
    } else {
      this.changeToOrganiserAdmin(administrator);
    }
  }
}
